import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { Button } from '@/components/ui/button'

export interface LLMConfig {
  temperature: number
  topP: number
  presencePenalty: number
  frequencyPenalty: number
}

interface Step3LLMConfigProps {
  config: LLMConfig
  onConfigChange: (config: LLMConfig) => void
}

const DEFAULT_CONFIG: LLMConfig = {
  temperature: 0.3,
  topP: 0.3,
  presencePenalty: 0.4,
  frequencyPenalty: 0.7,
}

export function Step3LLMConfig({ config, onConfigChange }: Step3LLMConfigProps) {
  const handleReset = () => {
    onConfigChange(DEFAULT_CONFIG)
  }

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold mb-1">Language Model Settings</h3>
        <p className="text-sm text-muted-foreground">
          Configure how the AI model generates responses
        </p>
      </div>

      {/* Model (Disabled) */}
      <div className="space-y-2">
        <Label htmlFor="model">Model</Label>
        <Input
          id="model"
          value="Managed by ChatterPillar"
          disabled
          className="bg-muted"
        />
        <p className="text-xs text-muted-foreground">
          Model selection is configured at the organization level
        </p>
      </div>

      {/* Temperature */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <Label>Temperature</Label>
          <span className="text-sm font-medium">{config.temperature.toFixed(1)}</span>
        </div>
        <Slider
          value={[config.temperature]}
          onValueChange={([value]) => onConfigChange({ ...config, temperature: value })}
          min={0}
          max={1}
          step={0.1}
        />
        <p className="text-xs text-muted-foreground">
          Controls randomness. Lower = more focused, Higher = more creative
        </p>
      </div>

      {/* Top P */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <Label>Top P</Label>
          <span className="text-sm font-medium">{config.topP.toFixed(1)}</span>
        </div>
        <Slider
          value={[config.topP]}
          onValueChange={([value]) => onConfigChange({ ...config, topP: value })}
          min={0}
          max={1}
          step={0.1}
        />
        <p className="text-xs text-muted-foreground">
          Nucleus sampling threshold. Lower = more conservative
        </p>
      </div>

      {/* Presence Penalty */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <Label>Presence Penalty</Label>
          <span className="text-sm font-medium">{config.presencePenalty.toFixed(1)}</span>
        </div>
        <Slider
          value={[config.presencePenalty]}
          onValueChange={([value]) => onConfigChange({ ...config, presencePenalty: value })}
          min={0}
          max={2}
          step={0.1}
        />
        <p className="text-xs text-muted-foreground">
          Reduces repetition of topics already discussed
        </p>
      </div>

      {/* Frequency Penalty */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <Label>Frequency Penalty</Label>
          <span className="text-sm font-medium">{config.frequencyPenalty.toFixed(1)}</span>
        </div>
        <Slider
          value={[config.frequencyPenalty]}
          onValueChange={([value]) => onConfigChange({ ...config, frequencyPenalty: value })}
          min={0}
          max={2}
          step={0.1}
        />
        <p className="text-xs text-muted-foreground">
          Reduces repetition of specific words/phrases
        </p>
      </div>

      {/* Reset Button */}
      <div className="pt-4">
        <Button type="button" variant="outline" onClick={handleReset}>
          Reset to Defaults
        </Button>
      </div>
    </div>
  )
}

export { DEFAULT_CONFIG as DEFAULT_LLM_CONFIG }
